import React, { forwardRef, useEffect, useLayoutEffect, useRef } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';

// Editor is an uncontrolled React component
const Editor = forwardRef(
  ({ readOnly, defaultValue, onTextChange, onSelectionChange }, ref) => {
    const containerRef = useRef(null);
    const defaultValueRef = useRef(defaultValue);
    const onTextChangeRef = useRef(onTextChange);
    const onSelectionChangeRef = useRef(onSelectionChange);

    useLayoutEffect(() => {
      // keep refs in sync with latest props
      defaultValueRef.current = defaultValue;
      onTextChangeRef.current = onTextChange;
      onSelectionChangeRef.current = onSelectionChange;
    });

    // When defaultValue prop changes after mount, update the quill contents
    useEffect(() => {
      const quillInstance = ref.current;
      if (!quillInstance) return;

      try {
        const newVal = defaultValueRef.current;
        const current = quillInstance.getContents();
        // avoid re-applying identical contents (prevents interfering with user typing)
        if (JSON.stringify(current) === JSON.stringify(newVal)) return;

        if (newVal) {
          quillInstance.setContents(newVal);
        } else {
          // clear editor if empty/null
          quillInstance.setContents([{ insert: '\n' }]);
        }
      } catch (e) {
        // ignore setContents errors
      }
    }, [defaultValue, ref]);

    useEffect(() => {
      ref.current?.enable(!readOnly);
    }, [ref, readOnly]);

    useEffect(() => {
      const container = containerRef.current;
      // ensure the outer container has a sensible minimum height so the editor is multiline by default
      if (container) container.style.minHeight = '300px';
      const editorContainer = container.appendChild(
        container.ownerDocument.createElement('div'),
      );
      // make the actual Quill editor element taller by default
      editorContainer.style.minHeight = '300px';
      const quill = new Quill(editorContainer, {
        theme: 'snow',
      });

      // Ensure the editor area is fully clickable by setting minHeight
      quill.root.style.minHeight = '300px';

      ref.current = quill;

      if (defaultValueRef.current) {
        quill.setContents(defaultValueRef.current);
      }

      quill.on(Quill.events.TEXT_CHANGE, (...args) => {
        onTextChangeRef.current?.(...args);
      });

      quill.on(Quill.events.SELECTION_CHANGE, (...args) => {
        onSelectionChangeRef.current?.(...args);
      });

      return () => {
        ref.current = null;
        container.innerHTML = '';
      };
    }, [ref]);

    return <div ref={containerRef} style={{ minHeight: '300px' }}></div>;
  },
);

Editor.displayName = 'Editor';

export default Editor;